import { Role } from '../types/auth.type';
import { canAccessWorkflowRole, hasAdminPanelAccess, INTERNAL_CHAT_ROLES } from '../utils/roles.utils';

/**
 * If true: show Mohasby AI tab only for ADMIN, beta emails, and allowed orgs.
 * If false: show for all org roles (as long as they have access_chat permission).
 */
export const RESTRICT_MOHASBY_AI_TAB = true;

/**
 * Additional hard deny-list (internal): these user IDs will NOT see Mohasby AI tab,
 * even if their role is otherwise allowed.
 */
export const MOHASBY_AI_TAB_DENY_USER_IDS = new Set<string>([
  '01K13F4A9B0D9GBM99J57JV7MR',
  '01K13F4C1RRT07RSVRVN4VARXV',
  '01K13F47JK7JXZCMFV0GKPSQHK',
  '01KDJ4FM5QG1JYYF9A8K2YN8RG',
  '01K13F48ESQBGPSES3R1X6RRW0',
  '01KD03555GRN4Y3823YVS8SR2J',
  '01K6XSG2WT8822SVE202JW108N',
]);

/** Beta testers (by user email) who can access Mohasby AI while RESTRICT_MOHASBY_AI_TAB is true. */
export const MOHASBY_AI_BETA_USER_EMAILS = new Set<string>([
  'anas@qwacks.com',
  'azzam@mohasby22.com',
]);

/** Organizations that can access Mohasby AI while RESTRICT_MOHASBY_AI_TAB is true. */
export const MOHASBY_AI_ALLOWED_ORG_IDS = new Set<string>([
  '01KQF7AX11KM3B6H5JKXT0H6CN',
  '01KX31VST9185CM0D7ZAWGC21B',
  '01KVSZP9J5PYD68ABWB4T0ERKT',
  '01KQ4DPG5RG671M78YXNH9FAXH',
  '01KB29M6J86K2NVDRPYGEYFG3H',
  '01K5E08YCXTBS366APSSH5A9H1',
  '01K1AMBSQJW0AWT1T86YMZ77EH',
  '01K4SH8KTV6DX2SJRY3PC2BRDD',
  '01KDSZBM6KBCG2MVCPPDSX5MGV',
  '01M0A4RSVGN35G9BX6KHJA49RZ',
  '01KJR53GZN3J3F8PZPJDXNCY5Y',
  '01K1AMBQZ8Z23Q9BD8T0JRDE5D',
  '01KBSJHBJEKFT6WRB7DBPFJ52F',
  '01KDWW5BV0NK4K0BQD5G8VZX39',
  '01KDJRW21CVTQSKZ0KN3X004H3',
  '01KT1FXE3J7YGNTPYMY1XM0NR3',
  '01KAGPSNYDRX4FSST89C4MV3QZ',
  '01KDQB3GDJ32HC9JJDKQS2S9QJ',
  '01K9VQNPPHV2AFWAR348XQ61A1',
  '01KPX8QKQA5JBAMDWKCJDWYERY',
  '01KP6VAFQA8QV3KT8KH67W8DJN',
]);

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase() ?? '';

export const isMohasbyAiBetaUser = (userEmail?: string | null) =>
  MOHASBY_AI_BETA_USER_EMAILS.has(normalizeEmail(userEmail));

export const isMohasbyAiAllowedOrg = (organizationId?: string | null) =>
  !!organizationId && MOHASBY_AI_ALLOWED_ORG_IDS.has(organizationId);

export const getMohasbyAiAllowedRoles = (): Role[] => {
  if (RESTRICT_MOHASBY_AI_TAB) {
    return [
      Role.ADMIN,
      Role.CUSTOMER_SUCCESS,
      Role.SECTOR_OPERATIONS_MANAGER,
      Role.ACCOUNTANT,
      Role.CLEARING_OFFICER,
      Role.SALES,
      Role.ORGANIZATION,
      Role.ORGANIZATION_USER,
    ];
  }
  return [Role.ADMIN, Role.ACCOUNTANT, Role.CLEARING_OFFICER, Role.SALES, Role.ORGANIZATION, Role.ORGANIZATION_USER];
};

export const canUserSeeMohasbyAiTab = (userId?: string | null) => {
  if (!userId) return false;
  return !MOHASBY_AI_TAB_DENY_USER_IDS.has(userId);
};

export const canAccessMohasbyAI = (
  role?: Role,
  userId?: string | null,
  userEmail?: string | null,
  organizationId?: string | null,
) => {
  if (!canUserSeeMohasbyAiTab(userId)) return false;
  if (role === Role.ADMIN) return true;
  if (isMohasbyAiBetaUser(userEmail)) return true;
  if (isMohasbyAiAllowedOrg(organizationId)) return true;
  if (!RESTRICT_MOHASBY_AI_TAB) return true;
  return false;
};

/** Admin panel + accountants, clearing officers, and sector operations managers. */
export const canAccessWorkflow = (role?: Role, _userEmail?: string | null): boolean => {
  return canAccessWorkflowRole(role);
};

/** Staff roles with access to Internal Chat (admin/CS use inbox; others use org chat). */
export const canAccessInternalChat = (role?: Role, _userEmail?: string | null): boolean => {
  if (!role) return false;
  return INTERNAL_CHAT_ROLES.includes(role);
};

export const getInternalChatNavPath = (role?: Role): string =>
  hasAdminPanelAccess(role) ? '/dashboard/internal-chat' : '/dashboard/org-internal-chat';

